import { ParentSize } from '@visx/responsive'
import moment from 'moment'
import { ReactElement, useMemo } from 'react'
import BarStackGraph from 'app/components/viz/graphs/BarStackGraph'
import { formatUsdOrDash } from 'app/components/usage-tracking/usageCostFormat'

export type UsageGranularity = `hourly` | `daily` | `weekly` | `monthly`

export type UsageTimeSeriesBucket = {
  bucketStart: string
  voiceCostUsd: number
  smsCostUsd: number
}

const VOICE_KEY = `Voice Agent`
const SMS_KEY = `SMS Agent`
const ALL_KEYS = [VOICE_KEY, SMS_KEY]

// Match the current product-tab identity: Tailwind blue-500 for Voice,
// teal-400 for SMS. Carried over unchanged from the previous hand-rolled
// chart so the dashboard's visual identity doesn't shift as part of this
// refactor.
const COLOR_MAP: Record<string, string> = {
  [VOICE_KEY]: `#3B82F6`,
  [SMS_KEY]: `#2DD4BF`,
}

// `BarStackGraph` keys each bar by the `day` string (its x accessor and React
// key), so two buckets with the same formatted label would merge into one
// bar. Within a single selected date range these per-granularity formats are
// unique: hourly bakes the date into the label (so it doesn't collide across
// days), and daily/weekly/monthly buckets are inherently one-per-period
// within any range a user can select. The previous hand-rolled version kept
// this safe by keying off the raw `bucketStart` timestamp instead of the
// formatted label — this comment documents the same invariant now that the
// formatted label is itself the key.
function formatBucketLabel(bucketStart: string, granularity: UsageGranularity): string {
  switch (granularity) {
    case `hourly`:
      return moment(bucketStart).format(`MMM D, h A`)
    case `daily`:
      return moment(bucketStart).format(`MMM D`)
    case `weekly`:
      return moment(bucketStart).format(`MMM D`)
    case `monthly`:
      return moment(bucketStart).format(`MMM YYYY`)
  }
}

// Always includes the date, even for granularities whose axis label omits it,
// so the tooltip stays unambiguous regardless of formatBucketLabel's format.
function formatTooltipLabel(bucketStart: string, granularity: UsageGranularity): string {
  switch (granularity) {
    case `hourly`:
      return moment(bucketStart).format(`MMM D, YYYY h A`)
    case `monthly`:
      return moment(bucketStart).format(`MMMM YYYY`)
    default:
      return moment(bucketStart).format(`MMM D, YYYY`)
  }
}

export function UsageCostChart({
  data,
  granularity,
}: {
  data: UsageTimeSeriesBucket[]
  granularity: UsageGranularity
}): ReactElement {
  const chartData = useMemo(
    () =>
      data.map((bucket) => ({
        day: formatBucketLabel(bucket.bucketStart, granularity),
        tooltipDisplay: formatTooltipLabel(bucket.bucketStart, granularity),
        values: { [VOICE_KEY]: bucket.voiceCostUsd, [SMS_KEY]: bucket.smsCostUsd },
      })),
    [data, granularity],
  )

  const totals = useMemo(
    () => ({
      [VOICE_KEY]: data.reduce((sum, bucket) => sum + bucket.voiceCostUsd, 0),
      [SMS_KEY]: data.reduce((sum, bucket) => sum + bucket.smsCostUsd, 0),
    }),
    [data],
  )

  const minChartWidthClass = granularity === `hourly` ? `min-w-[2400px]` : `min-w-[1080px]`

  return (
    <div className="rounded-lg border border-default p-4">
      <div className="overflow-x-auto">
        <div className={`h-[360px] ${minChartWidthClass}`}>
          <ParentSize>
            {({ width, height }) =>
              width && height ? (
                <BarStackGraph
                  width={width}
                  height={height}
                  data={chartData}
                  allKeys={ALL_KEYS}
                  totals={totals}
                  colorMap={COLOR_MAP}
                  labelPrecision={2}
                  showBarLabels={false}
                  thinDesktopLabels
                  valueFormatter={formatUsdOrDash}
                />
              ) : null
            }
          </ParentSize>
        </div>
      </div>
    </div>
  )
}
